import { ReactElement, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth, useHasAccess } from '../../hooks/auth'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import {
  Container,
  ExceptionContainer,
  EventTitle,
  MetaRow,
  MetaItem,
  MetaDot,
  HeaderDivider,
  QuickAccessSection,
  QuickAccessLabel,
  QuickAccessButtons,
  QuickButton,
  QuickButtonIcon,
  Section,
  SectionHeader,
  SectionTitle,
  SectionBadge,
  SectionRule,
  CardsGrid,
} from './styles'

import Loader from '../../components/Loader'
import { parseISO, format, setDefaultOptions } from 'date-fns'
import pt from 'date-fns/locale/pt-BR'
import { EventInfo, useEvent } from '../../hooks/events'
import { useGetProducerPDV } from '../../hooks/producer-pdv'
import {
  useEventActions,
  EventActionCategory,
  EventActionItem,
} from '../../hooks/navigation'
import useBdAppEventBus, {
  WebviewConfig,
} from '../../hooks/bdapp/useBdAppEventBus'
import useBdApp from '../../hooks/bdapp/useBdApp'
import { Breadcrumb } from '../../components/Breadcrumb'
import MailingDownloadButton from '../../components/MailingDownloadButton'
import CouponsButton from '../../components/CouponsButton'
import { EventOptionCard } from '../../components/EventOptionCard'

import iconCalendar from '../../assets/event-options/icon-calendar.svg'
import iconMapPin from '../../assets/event-options/icon-map-pin.svg'
import iconUser from '../../assets/event-options/icon-user.svg'
import iconZap from '../../assets/event-options/icon-zap.svg'
import btnDashboard from '../../assets/event-options/btn-dashboard.svg'
import btnWallet from '../../assets/event-options/btn-wallet.svg'
import btnChart from '../../assets/event-options/btn-chart.svg'
import btnTicketCheck from '../../assets/event-options/btn-ticket-check.svg'
import cardFinancialClose from '../../assets/event-options/card-financial-close.svg'
import cardWithdraw from '../../assets/event-options/card-withdraw.svg'
import cardReceiptsCalendar from '../../assets/event-options/card-receipts-calendar.svg'
import cardOs from '../../assets/event-options/card-os.svg'
import cardDashboard from '../../assets/event-options/card-dashboard.svg'
import cardSalesReport from '../../assets/event-options/card-sales-report.svg'
import cardAccessReport from '../../assets/event-options/card-access-report.svg'
import cardAbandonedCart from '../../assets/event-options/card-abandoned-cart.svg'
import cardTagsPixels from '../../assets/event-options/card-tags-pixels.svg'
import cardValidator from '../../assets/event-options/card-validator.svg'

const SECTION_ORDER: EventActionCategory[] = [
  'financeiro',
  'relatorios',
  'marketing',
  'ferramentas',
]

const SECTION_TITLES: Record<EventActionCategory, string> = {
  financeiro: 'Financeiro',
  relatorios: 'Relatórios',
  marketing: 'Marketing',
  ferramentas: 'Ferramentas',
}

// Ícones do redesign (Figma OP-3470) por ação; ações sem asset caem
// no iconName do bd-web-components renderizado em vermelho.
const ACTION_ICONS: Record<string, string> = {
  financial: cardFinancialClose,
  withdraw: cardWithdraw,
  'receipts-calendar': cardReceiptsCalendar,
  os: cardOs,
  dashboard: cardDashboard,
  'sales-sumary': cardSalesReport,
  'access-sumary': cardAccessReport,
  'abandoned-cart': cardAbandonedCart,
  pixel: cardTagsPixels,
  validador: cardValidator,
}

export function Event(): ReactElement {
  const authContext = useAuth()
  const navigate = useNavigate()
  const { authHookWithOutEvent } = authContext

  // AUTH SECTION
  if (!LOCAL_EXECUTION) {
    authHookWithOutEvent()
  }

  // ----------------------------------------------------

  window.document.title = 'Detalhes do evento'
  const { id } = useParams<{ id: string }>()
  const fetchEvent = useEvent(Number(id))
  const event = useMemo(
    () => (fetchEvent.event || {}) as EventInfo,
    [fetchEvent]
  )
  const { publish } = useBdAppEventBus()
  const { isNative } = useBdApp()

  const accessDashboard = useHasAccess('DP2')
  const accesswithdraw =
    useHasAccess('DP3') &&
    (event?.clienteId === authContext?.user?.clienteId ||
      authContext?.user?.clienteId == null)
  const accessSalesSummary = useHasAccess('DP15')
  const accessMailingExport = useHasAccess('DP16')
  const accessValidador = useHasAccess('DP18')
  const accessCoupons = useHasAccess('DP21')

  const { producerPDV, producerPDVLoading, producerPDVError } =
    useGetProducerPDV(id as string)

  // useEffects ------------------------------------------
  useEffect(() => {
    if (event && event.nome) {
      window.document.title = `${event?.nome} - Detalhes do evento`
    }
  }, [event])

  const actions = useEventActions(id)

  useEffect(() => {
    if (isNative && event?.nome && id) {
      // O app nativo abre a primeira aba por padrão — as tabs seguem a
      // ordem do Acesso rápido (Dashboard primeiro), depois as demais
      // ações na ordem das seções da página.
      const quickAccessKeys = [
        'dashboard',
        'withdraw',
        'sales-sumary',
        'validador',
      ]
      const orderedActions = [
        ...quickAccessKeys
          .map((key) => actions.find((a) => a.key === key))
          .filter((a): a is EventActionItem => a !== undefined),
        ...actions.filter((a) => !quickAccessKeys.includes(a.key)),
      ]
      const tabs: WebviewConfig[] = orderedActions.map((a) => ({
        id: a.key,
        icon: a.iconName,
        title: a.title,
        url: new URL(a.to, window.location.origin).href,
      }))

      publish('event-selected', {
        id: Number(id),
        name: event.nome,
      })
      publish('tabs-create', tabs)
    }
  }, [isNative, id, event, publish, actions])

  useEffect(() => {
    if (fetchEvent.event !== undefined) {
      setDefaultOptions({ locale: pt })
    }
  }, [fetchEvent])

  useEffect(() => { }, [producerPDV, producerPDVLoading, producerPDVError])

  const quickAccess = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: btnDashboard,
      to: `/events/${id}/dashboard`,
      visible: accessDashboard,
    },
    {
      key: 'withdraw',
      label: 'Solicitar saque',
      icon: btnWallet,
      to: `/events/${id}/withdraw`,
      visible: accesswithdraw,
    },
    {
      key: 'sales-sumary',
      label: 'Relatório de vendas',
      icon: btnChart,
      to: `/events/${id}/sales-sumary`,
      visible: accessSalesSummary,
    },
    {
      key: 'validador',
      label: 'Validar ingressos',
      icon: btnTicketCheck,
      to: `/events/${id}/validador`,
      visible: accessValidador,
      primary: true,
    },
  ].filter((q) => q.visible)

  const sectionCount = (category: EventActionCategory): number => {
    const extra =
      category === 'marketing'
        ? (accessCoupons ? 1 : 0) + (accessMailingExport ? 1 : 0)
        : 0
    return actions.filter((a) => a.category === category).length + extra
  }

  return (
    <>
      <Container>
        {!fetchEvent.isLoading && (
          <>
            <Breadcrumb
              paths={[
                { name: 'Início', path: '/events' },
                { name: 'Evento', path: `/events/${id}` },
                { name: event?.nome, path: '#' },
              ]}
            />
            <EventTitle>
              {event?.data && `${format(parseISO(event.data), 'dd/MM')} — `}
              {event?.nome}
            </EventTitle>
            <MetaRow>
              {event?.data && (
                <MetaItem>
                  <img src={iconCalendar} alt="" />
                  {`Evento em ${format(
                    parseISO(event.data),
                    "dd 'de' MMMM, yyyy"
                  )}`}
                </MetaItem>
              )}
              {event?.local && (
                <>
                  <MetaDot />
                  <MetaItem>
                    <img src={iconMapPin} alt="" />
                    {event.local}
                  </MetaItem>
                </>
              )}
              {event?.cliente && (
                <>
                  <MetaDot />
                  <MetaItem>
                    <img src={iconUser} alt="" />
                    {event.cliente}
                  </MetaItem>
                </>
              )}
            </MetaRow>
            <HeaderDivider />

            {quickAccess.length > 0 && (
              <QuickAccessSection>
                <QuickAccessLabel>
                  <img src={iconZap} alt="" />
                  Acesso rápido
                </QuickAccessLabel>
                <QuickAccessButtons>
                  {quickAccess.map((q) => (
                    <QuickButton
                      key={q.key}
                      $primary={q.primary}
                      onClick={() => navigate(q.to)}
                    >
                      <QuickButtonIcon>
                        <img src={q.icon} alt="" />
                      </QuickButtonIcon>
                      {q.label}
                    </QuickButton>
                  ))}
                </QuickAccessButtons>
              </QuickAccessSection>
            )}

            {SECTION_ORDER.map((category) => {
              const items = actions.filter((a) => a.category === category)
              const count = sectionCount(category)
              if (count === 0) return null

              return (
                <Section key={category}>
                  <SectionHeader>
                    <SectionTitle>{SECTION_TITLES[category]}</SectionTitle>
                    <SectionBadge>{count}</SectionBadge>
                    <SectionRule />
                  </SectionHeader>
                  <CardsGrid>
                    {category === 'marketing' && accessCoupons && (
                      <CouponsButton eventId={id} />
                    )}
                    {category === 'marketing' && accessMailingExport && (
                      <MailingDownloadButton eventId={id} />
                    )}
                    {items.map((a) => (
                      <EventOptionCard
                        key={a.key}
                        title={a.title}
                        description={a.description}
                        iconSrc={ACTION_ICONS[a.key]}
                        iconName={a.iconName}
                        badge={a.badge}
                        onClick={() => navigate(a.to)}
                      />
                    ))}
                  </CardsGrid>
                </Section>
              )
            })}
          </>
        )}
        {fetchEvent.isLoading && (
          <ExceptionContainer height={400} margin={false}>
            <Loader />
          </ExceptionContainer>
        )}
      </Container>
    </>
  )
}
